import PropTypes from 'prop-types';
import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkSubSuper from 'remark-sub-super';
import FICOStyles from './FICO.styled';
import Layout from '../../components/Layout/Layout';
import SEO from '../../components/SEO/SEO';
import Heading from '../../components/Heading/Heading';
import Accordion from '../../components/Accordion/Accordion';
import Content from '../../components/Content/Content';
import UsefulLinks from '../../components/Shared/UsefulLinks';
import FileTextIcon from '../../components/Shared/Icons/FileText';
import Picture from '../../components/Shared/Picture';
import Disclaimer from '../../components/Disclaimer/Disclaimer';
import FicoScoresTable from '../../components/Shared/FicoScoresTable';
import { getPageContent } from '../../services/dpc';
import theme from '../../styles/theme';
import useGTM from '../../hooks/useGTM';
import Error from '../../components/Error/Error';

function FICO({
  data: { titleTag, metaDescription, pageTitle, heroText, disclaimer } = {},
  error,
  className,
  isLoading,
}) {
  if (error) {
    console.error(error);
    return (
      <Layout>
        <Error />
      </Layout>
    );
  }

  const [triggerGTMEvent] = useGTM();
  const usefulLinks = [
    {
      title: (
        <>
          FICO<sup>®</sup> Scores FAQ
        </>
      ),
      to: 'https://ficoscore.com/wp-content/uploads/Frequently-Asked-Questions-About-FICO-Scores.pdf',
      isExternal: true,
      newTab: true,
      icon: <FileTextIcon />,
      onClick: triggerGTMEvent({
        name: 'vmf.cta_click',
        location: 'FICO Score',
        category: 'loan_education',
        action: 'text',
        label: 'fico_exit_faq',
      }),
    },
    {
      title: (
        <>
          Understanding FICO<sup>®</sup> Scores
        </>
      ),
      to: 'https://ficoscore.com/wp-content/uploads/Understanding-FICO-Scores.pdf',
      isExternal: true,
      newTab: true,
      icon: <FileTextIcon />,
      onClick: triggerGTMEvent({
        name: 'vmf.cta_click',
        location: 'FICO Score',
        category: 'loan_education',
        action: 'text',
        label: 'fico_exit_learn',
      }),
    },
  ];
  const faqs = [
    {
      title: 'Why is Vanderbilt providing me with my FICO^®^ Score?',
      answer:
        'We think it’s important to provide our customers with access to information that will help them understand and stay on top of their credit status. That’s why we are providing you with your FICO^®^ Score and information to help you understand it. It is required by the Fair Credit Reporting Act in certain circumstances.',
      onExpand: triggerGTMEvent({
        name: 'vmf.cta_click',
        location: 'FICO Score',
        category: 'frequently_asked',
        action: 'summary',
        label: 'why_vmf_fico',
      }),
      onReadMore: triggerGTMEvent({
        name: 'vmf.cta_click',
        location: 'FICO Score',
        category: 'frequently_asked',
        action: 'text',
        label: 'why_vmf_fico',
      }),
    },
    {
      title: 'Where did Vanderbilt get my FICO^®^ Score?',
      answer:
        'Vanderbilt partners with FICO^®^ and TransUnion^®^, one of the major credit reporting agencies, to be able to provide you with your FICO^®^ Score.',
      onExpand: triggerGTMEvent({
        name: 'vmf.cta_click',
        location: 'FICO Score',
        category: 'frequently_asked',
        action: 'summary',
        label: 'where_fico',
      }),
      onReadMore: triggerGTMEvent({
        name: 'vmf.cta_click',
        location: 'FICO Score',
        category: 'frequently_asked',
        action: 'text',
        label: 'where_fico',
      }),
    },
    {
      title:
        'Why is this score different from other scores that I received from somewhere else?',
      answer:
        'The FICO^®^ Score Vanderbilt provides is based on information from your TransUnion^®^ credit report based on the “as of” date included with your score. This may differ from scores you obtain elsewhere that may have been calculated at a different time using information from a different credit reporting agency or even a different score model. If you have additional questions regarding the FICO^®^ Score model and how it’s calculated, please refer to the FICO^®^ Score FAQ and Understanding FICO^®^ Scores links under the “Useful Links” section above.',
      onExpand: triggerGTMEvent({
        name: 'vmf.cta_click',
        location: 'FICO Score',
        category: 'frequently_asked',
        action: 'summary',
        label: 'why_different_fico',
      }),
      onReadMore: triggerGTMEvent({
        name: 'vmf.cta_click',
        location: 'FICO Score',
        category: 'frequently_asked',
        action: 'text',
        label: 'why_different_fico',
      }),
    },
    {
      title: 'I don’t agree with my FICO^®^ Score or Key Factors.',
      answer: `
  Your FICO^®^ Score and Key Factors were provided to Vanderbilt as they correspond to information within your TransUnion^®^ credit report based on the calculation date (i.e., “as of” date). If you feel this information is inaccurate, you can request a free annual credit report from TransUnion as well as the other two major credit reporting agencies at [www.annualcreditreport.com](https://www.annualcreditreport.com/ "Annual Credit Report"). If you have additional questions after reviewing, please contact the credit reporting agency at the number provided within the applicable credit report. You will be able to speak with a representative for additional help. Be sure to have the reference number included with your credit report when speaking with TransUnion or the other credit reporting agencies.
      
  If you think there is an error in your credit report, you can follow the directions in this Federal Trade Commission [publication](https://www.consumer.ftc.gov/articles/0151-disputing-errors-credit-reports) explaining how to dispute errors.
      `,
      onExpand: triggerGTMEvent({
        name: 'vmf.cta_click',
        location: 'FICO Score',
        category: 'frequently_asked',
        action: 'summary',
        label: 'dont_agree_fico',
      }),
      onReadMore: triggerGTMEvent({
        name: 'vmf.cta_click',
        location: 'FICO Score',
        category: 'frequently_asked',
        action: 'text',
        label: 'dont_agree_fico',
      }),
    },
    {
      title: 'I don’t see my FICO^®^ Score when I log onto my account.',
      answer: `
  Your score won’t be available if:
  - You recently opened a new account
  - You’re an authorized user on someone else’s account
  - The billing statement is not in your name
      `,
      onExpand: triggerGTMEvent({
        name: 'vmf.cta_click',
        location: 'FICO Score',
        category: 'frequently_asked',
        action: 'summary',
        label: 'dont_see_fico',
      }),
      onReadMore: triggerGTMEvent({
        name: 'vmf.cta_click',
        location: 'FICO Score',
        category: 'frequently_asked',
        action: 'text',
        label: 'dont_see_fico',
      }),
    },
  ];

  return (
    <Layout className={className} isLoading={isLoading}>
      <SEO title={titleTag} description={metaDescription} />
      <Heading background={theme.colors.blue10} appearance='dark'>
        <ReactMarkdown
          source={`# ${pageTitle}`}
          plugins={[remarkSubSuper]}
          renderers={{
            sub: 'sub',
            sup: 'sup',
          }}
        />
        <ReactMarkdown
          source={heroText}
          renderers={{
            paragraph: ({ children }) => (
              <p className='type-caption'>{children}</p>
            ),
            sub: 'sub',
            sup: 'sup',
          }}
          plugins={[remarkSubSuper]}
        />
      </Heading>

      <Content divider={false}>
        <h2>
          What are FICO<sup>®</sup> Scores?
        </h2>
        <p>
          According to credit industry analysts, the credit scores most widely
          used in lending decisions are FICO<sup>®</sup> Scores, the credit
          scores created by FICO, formerly known as Fair Isaac<sup>®</sup>{' '}
          Corporation. Lenders can request FICO<sup>®</sup> Scores from all
          three major consumer reporting agencies (CRAs), and use them to help
          them make credit decisions. FICO develops FICO<sup>®</sup> Scores
          based solely on information in consumer credit files compiled and
          maintained by the three major CRAs.
        </p>
        <p>
          Understanding your FICO<sup>®</sup> Score can help you better
          understand your credit risk and allow you to more responsibly manage
          your financial health.
        </p>
        <p className='margin-bottom-12'>
          A good FICO<sup>®</sup> Score means better financial options for you.
        </p>
        <UsefulLinks links={usefulLinks} />
        <Picture
          src='https://images.ctfassets.net/y64yks2od8ab/1Vj0xCdCDM8LcXbQEvIFiR/8fefec7357cdb89ba3ac60d34294f5f8/FICO_Scores.png?w=700'
          alt='FICO Scores'
          isResponsive
          sources={[
            {
              media: '(min-width: 1440px)',
              params: '?w=700',
              srcSet:
                'https://images.ctfassets.net/y64yks2od8ab/1Vj0xCdCDM8LcXbQEvIFiR/8fefec7357cdb89ba3ac60d34294f5f8/FICO_Scores.png',
            },
            {
              media: '(min-width: 512px)',
              params: '?w=512',
              srcSet:
                'https://images.ctfassets.net/y64yks2od8ab/1Vj0xCdCDM8LcXbQEvIFiR/8fefec7357cdb89ba3ac60d34294f5f8/FICO_Scores.png',
            },
            {
              media: '(max-width: 512px)',
              params: '?w=300',
              srcSet:
                'https://images.ctfassets.net/y64yks2od8ab/1Vj0xCdCDM8LcXbQEvIFiR/8fefec7357cdb89ba3ac60d34294f5f8/FICO_Scores.png',
            },
          ]}
        />
        <h2>
          What is a good FICO<sup>®</sup> Score?
        </h2>
        <p>
          The minimum credit score needed to qualify for a particular loan
          program and/or Annual Percentage Rate (APR) is known as the “score
          cutoff”. Since the score cutoff varies by lender and loan product,
          it's hard to say what a good FICO<sup>®</sup> Score is outside the
          context of a particular lending decision. For example, one mortgage
          lender may have a score cutoff (or minimum credit score) of 680 for a
          30 year fixed rate loan with a 4% APR and another lender may have a
          score cutoff of 720 for the same loan product and APR. A licensed loan
          originator should be able to advise you regarding specific score
          cutoffs for the various loan programs and APRs.
        </p>
        <p>
          The chart below provides a breakdown of ranges for FICO<sup>®</sup>{' '}
          Scores found across the U.S. consumer population. It provides general
          guidance on what a particular FICO<sup>®</sup> Score represents.
          Again, each lender has its own credit risk standards.
        </p>

        <FicoScoresTable />
      </Content>
      <Accordion items={faqs} />
      <Disclaimer appearance='dark' background={theme.colors.white}>
        <ReactMarkdown
          source={disclaimer}
          plugins={[remarkSubSuper]}
          renderers={{
            sub: 'sub',
            sup: 'sup',
          }}
        />
      </Disclaimer>
    </Layout>
  );
}

FICO.getInitialProps = async ({}) => {
  try {
    const dpc = await getPageContent('/fico-score/');

    if (dpc.status !== 200) {
      throw dpc.statusText;
    }

    return { data: dpc.data.result };
  } catch (error) {
    // Handle issues loading data
    if (error.response.status === 404) return { statusCode: 404 };
    return { error };
  }
};

FICO.propTypes = {
  match: PropTypes.object,
  history: PropTypes.object,
  location: PropTypes.object,
  data: PropTypes.shape({}),
  error: PropTypes.object,
};

export default FICOStyles(FICO);
